import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import HideDraftedControl from './HideDraftedControl';
import AvailabilitySegment from './AvailabilitySegment';
import { POOL_POSITION_FILTERS, type PoolPositionFilter } from '@/lib/filterDraftPool';

/**
 * SAB-932: search, position filter and the hide-drafted toggle for the V2 player pool.
 *
 * ── WHY ALL THREE ARRIVED AT ONCE ───────────────────────────────────────────
 *
 * SAB-932 asked only for the hide toggle. It could not ship alone here, because V2 had
 * NEITHER search NOR a position filter -- the pool was an unfiltered, unsearchable list of
 * ~2,500 rows. The ticket's own constraint is "hiding must not break search", and in a room
 * with no search a hide makes a taken player unfindable outright, which is worse than the
 * complaint being fixed. So search is the dependency, not a nicety: it is what makes hiding
 * safe. Position filter came with it because the legacy room has ten options and a manager
 * browsing 2,500 rows needs it for the same reason.
 *
 * ── THE FILTERING IS NOT DONE HERE ──────────────────────────────────────────
 *
 * This component is inputs only. The rule -- drafted are hidden while BROWSING and always
 * shown while SEARCHING -- lives in `filterDraftPool`, shared with the legacy room so the
 * two cannot drift while both are live (SAB-931). Putting the predicate in a toolbar
 * component would have given each room its own copy of the reconciliation between SAB-843
 * and SAB-932, which is exactly the thing that must not happen twice.
 *
 * ── THE LABEL STATES THE CURRENT STATE, NOT THE NEXT ONE ────────────────────
 *
 * "HIDE DRAFTED" lit means drafted ARE hidden. The common alternative -- labelling a
 * control with the action it performs -- reads as the opposite when the control is also a
 * status light, and this one is both.
 */

export interface PoolControlsProps {
  query: string;
  onQueryChange: (q: string) => void;
  posFilter: PoolPositionFilter;
  onPosFilterChange: (p: PoolPositionFilter) => void;
  hideDrafted: boolean;
  onHideDraftedChange: (hidden: boolean) => void;
  /** How many rows the hide is withholding. 0 while searching. */
  hiddenDraftedCount: number;
  /**
   * SAB-955: the tabs this league offers, from `poolPositionFiltersFor`. Defaults to the
   * full list so a caller that has not wired startability yet is unchanged.
   */
  positionFilters?: readonly PoolPositionFilter[];
  /**
   * SAB-959: 'stacked' is the mobile Players tab -- a full-width search row above a single
   * scrolling chip strip with the ALL/AVAILABLE segment pinned beside it. 'inline' is the
   * original desktop toolbar and stays the default, so that room is untouched.
   *
   * A LAYOUT variant only. Both share one search box, one chip list and one availability
   * control, so neither the filter behaviour nor the persisted hideDrafted value forks.
   */
  layout?: 'inline' | 'stacked';
  className?: string;
}

export default function PoolControls({
  query, onQueryChange, posFilter, onPosFilterChange,
  hideDrafted, onHideDraftedChange, hiddenDraftedCount,
  positionFilters = POOL_POSITION_FILTERS, layout = 'inline', className,
}: PoolControlsProps) {
  const searching = query.trim().length > 0;

  if (layout === 'stacked') {
    return (
      <div data-testid="pool-controls" className={cn('flex flex-col gap-[10px]', className)}>
        <div className="relative">
          <Search className="pointer-events-none absolute left-[12px] top-1/2 h-[16px] w-[16px] -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="pool-search"
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search players"
            aria-label="Search players"
            className={cn(
              'h-[44px] w-full rounded-[12px] border border-border bg-card pl-[36px] pr-[36px]',
              'text-[14px] text-foreground placeholder:text-muted-foreground',
              'focus:outline-none focus:border-primary/50',
            )}
          />
          {searching && (
            <button
              data-testid="pool-search-clear"
              onClick={() => onQueryChange('')}
              aria-label="Clear search"
              className="absolute right-[6px] top-1/2 h-[32px] w-[32px] -translate-y-1/2 flex items-center justify-center text-muted-foreground"
            >
              <X className="h-[16px] w-[16px]" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-[8px]">
          {/*
            ONE scrolling line, not a wrapping grid. `min-w-0` is what lets it actually
            shrink inside the flex row -- without it the strip sizes to its content and
            pushes the segment off the edge instead of scrolling.

            The strip is 44px tall while the chips are 32px, so each chip's 44px touch
            overlay fits INSIDE it. A taller-than-strip overlay would have given an
            `overflow-x-auto` box a vertical scrollbar too, since a non-visible overflow on
            one axis makes the other axis auto.
          */}
          <div
            data-testid="pool-pos-filters"
            className={cn(
              'flex h-[44px] min-w-0 flex-1 items-center gap-[6px] overflow-x-auto',
              '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            )}
          >
            {positionFilters.map((pos) => (
              <button
                key={pos}
                data-testid={`pool-pos-${pos}`}
                data-active={posFilter === pos || undefined}
                // src/index.css:411 floors every button at 44x44. The design's chip is 32px
                // tall, so it opts out here and buys the tap target back with the overlay
                // below -- 32px of chip plus 6px above and below is exactly 44.
                data-compact=""
                onClick={() => onPosFilterChange(pos)}
                className={cn(
                  'relative flex-none rounded-full border px-[12px] h-[32px]',
                  'font-display text-[11.5px] font-bold tracking-[0.06em] transition-colors',
                  'after:absolute after:inset-x-0 after:inset-y-[-6px] after:content-[""]',
                  posFilter === pos
                    ? 'bg-primary border-primary text-white'
                    : 'bg-transparent border-border text-muted-foreground',
                )}
              >
                {pos}
              </button>
            ))}
          </div>

          <span aria-hidden="true" className="h-[22px] w-px flex-none bg-border" />

          <AvailabilitySegment
            hideDrafted={hideDrafted}
            onChange={onHideDraftedChange}
            hiddenCount={hiddenDraftedCount}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pool-controls" className={cn('flex flex-wrap items-center gap-2', className)}>
      <div className="relative flex-none">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="pool-search"
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search players"
          aria-label="Search players"
          className={cn(
            'h-8 w-[168px] rounded-[9px] border border-border bg-muted/40 pl-7 pr-7',
            'text-[12px] text-foreground placeholder:text-muted-foreground',
            'focus:outline-none focus:border-primary/50',
          )}
        />
        {searching && (
          <button
            data-testid="pool-search-clear"
            onClick={() => onQueryChange('')}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div data-testid="pool-pos-filters" className="flex flex-wrap items-center gap-0.5">
        {positionFilters.map((pos) => (
          <button
            key={pos}
            data-testid={`pool-pos-${pos}`}
            data-active={posFilter === pos || undefined}
            onClick={() => onPosFilterChange(pos)}
            className={cn(
              'rounded px-2 py-0.5 font-display text-[10px] font-bold transition-colors border',
              posFilter === pos
                ? 'bg-primary/10 text-primary border-primary/20'
                : 'text-muted-foreground hover:text-foreground border-transparent',
            )}
          >
            {pos}
          </button>
        ))}
      </div>

      {/* SAB-950: shared with the V2 centre toolbar's AutoDraftControl, which sits
          adjacent to this one. See HideDraftedControl for why they must stay siblings. */}
      <HideDraftedControl
        hidden={hideDrafted}
        onChange={onHideDraftedChange}
        hiddenCount={hiddenDraftedCount}
      />
    </div>
  );
}
